import { ActionIcon, Group, NumberInput, Popover, Stack, Text, Tooltip } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCalendarEvent, IconCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRate, type Rate } from '@/api/catalog';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';
import dayjs from '@/lib/dayjs';
import { currentRate } from './rates';

import type { Kind, Level } from './rates';

interface Props {
  rates: Rate[];
  kind: Kind;
  level: Level;
  refId?: string;
  ref2Id?: string;
  placeholder?: string;
  disabled?: boolean;
  w?: number;
}

/**
 * Bearbeitung eines Satzes (RATE-01…03). Leer = vom übergeordneten Satz erben. Mit
 * Feature „HISTORIC_RATES“ kann ein Gültigkeitsdatum gewählt werden.
 */
export function RateField({ rates, kind, level, refId, ref2Id, placeholder, disabled, w = 140 }: Props) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const setRate = useSetRate(ctx.workspaceId);
  const onError = useApiError();
  const current = currentRate(rates, kind, level, refId, ref2Id);
  const [value, setValue] = useState<string | number>(current?.amount ?? '');
  const [from, setFrom] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => setValue(current?.amount ?? ''), [current?.amount]);

  const commit = (effectiveFrom: string | null) => {
    const amount = value === '' || value == null ? undefined : Number(value);
    if (amount === current?.amount && !effectiveFrom) return;
    setRate.mutate({ kind, level, refId, ref2Id, amount, effectiveFrom: effectiveFrom ?? undefined }, { onError });
    setOpen(false);
  };

  return (
    <Group gap={4} wrap="nowrap">
      <NumberInput
        value={value}
        onChange={setValue}
        onBlur={() => !open && commit(null)}
        min={0}
        decimalScale={2}
        fixedDecimalScale
        placeholder={placeholder}
        rightSection={<Text size="xs" c="dimmed">{ctx.workspace.currency}</Text>}
        rightSectionWidth={44}
        disabled={disabled}
        w={w}
        size="sm"
      />
      {ctx.hasFeature('HISTORIC_RATES') && !disabled && (
        <Popover opened={open} onChange={setOpen} withinPortal>
          <Popover.Target>
            <Tooltip label={t('rates.validFrom')}>
              <ActionIcon variant="subtle" color="gray" onClick={() => setOpen((o) => !o)} aria-label={t('rates.validFrom')}>
                <IconCalendarEvent size={16} />
              </ActionIcon>
            </Tooltip>
          </Popover.Target>
          <Popover.Dropdown>
            <Stack gap="xs">
              <Text size="sm">{t('rates.validFromHint')}</Text>
              <DatePickerInput value={from} onChange={(v) => setFrom(v ? dayjs(v).format('YYYY-MM-DD') : null)} clearable placeholder={t('rates.allTime')} />
              <ActionIcon onClick={() => commit(from)} aria-label={t('common.apply')}>
                <IconCheck size={16} />
              </ActionIcon>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      )}
    </Group>
  );
}
